import { UICheckButton, UIControl, UITextArea, UITextField, UIView, UIViewController, UISegmentedControl, UIApplication } from "uikit";
import { MUIComboBox } from "muikit";
import { StepView, StepViewProtocol } from "./StepView";
import { isValidEmail, DLDB } from "dldb";
import { AppDelegate } from "../AppDelegate";
import { NSLocalizeString } from "foundation";
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export interface BookingClientProtocolDelegate
{
  onClientFormDidChange ( vc: BookingClientViewController ) : void ;
}

export class BookingClientViewController extends UIViewController implements StepViewProtocol
{
    delegate         : BookingClientProtocolDelegate = null ;
    name             : string         = "" ;
    email            : string         = "" ;
    phone            : string         = "" ;
    phoneCountryISO2 : string         = "" ;
    message          : string         = "" ;
    allergies        : boolean        = false ;
    allergens        : string         = "" ;
    
    stepView: StepView;

    private nameTextField        : UITextField   = null ;
    private emailTexteField      : UITextField   = null ;
    private phoneTextField       : UITextField   = null ;
    private phoneCountryComboBox : MUIComboBox   = null ;

    private allergiesCB          : UICheckButton = null ;
    private allergiesTextArea    : UITextArea    = null ;
    private languageView         : UIView ;
    private languageComboBox     : MUIComboBox   = null ;
    private nationalityView      : UIView;
    private nationalityComboBox  : MUIComboBox   = null ;
    private messageTextArea      : UITextArea    = null ;
    private couponView           : UIView;
    private optionsView          : UIView;
    private segmentedControl     : UISegmentedControl;

    private errorNameView           : UIView = null ;
    private errorEmailView          : UIView = null ;
    private errorPhoneView          : UIView = null ;
    private errorNationalityView    : UIView = null ;
    private errorOptionsView        : UIView = null ;

    private phoneCountryCallingCodes : { [iso2: string]: string } = {} ;
    private phoneCountryNames        : { [iso2: string]: string } = {} ;


    viewDidLoad ( )
    {
        // name
        this.nameTextField.setOnChangeText( this, function(this:BookingClientViewController, control:UITextField, value:string) { this.onChangeText( "name", value ) } );
        
        // email
        this.emailTexteField.setOnChangeText( this, function(this:BookingClientViewController, control:UITextField, value:string) { this.onChangeText( "email", value ) } );
        
        // phone code
        if (this.phoneCountryComboBox) {
          this.phoneCountryComboBox.addTarget( this, this.onPhoneCountryChange, UIControl.Event.valueChanged );

          const selectEl = (this.phoneCountryComboBox.layer as any).selectElement as HTMLSelectElement ;
          if (selectEl) {
            selectEl.addEventListener( "change", () => {
              this.setPhoneCountrySelectedShortText( selectEl ) ;
            });
          }

          DLDB.shared.countries( (code: number, response: any) => {
            if (code === 200) {
              for (let country of response.data) {
                this.phoneCountryCallingCodes[ country.iso2 ] = country.phoneCode ;
                this.phoneCountryNames[ country.iso2 ]        = country.name ;
                this.phoneCountryComboBox.addItem( country.name + " (+" + country.callingCode + ")", country.iso2 );
              }
              this.phoneCountryComboBox.selectItem( DLDB.shared.defaultCountryCode );
              if (selectEl) this.setPhoneCountrySelectedShortText( selectEl ) ;
            }
          });
        }

        // phone text
        this.phoneTextField.setOnChangeText( this, function(this:BookingClientViewController, control:UITextField, value:string) {
          const countryCode = this.phoneCountryComboBox?.selectedItem ?? DLDB.shared.defaultCountryCode ;
          const parsedPhone = parsePhoneNumberFromString( value.trim(), countryCode ) ;
          if (parsedPhone != null && parsedPhone.isValid()) {
            let iso2 = parsedPhone.country;
            if (iso2 && this.phoneCountryComboBox) {
              this.phoneCountryComboBox.selectItem(iso2);
              const selectEl = (this.phoneCountryComboBox.layer as any).selectElement as HTMLSelectElement ;
              if (selectEl) this.setPhoneCountrySelectedShortText( selectEl ) ;
            }
            this.phoneCountryISO2 = iso2;
          }
          this.onChangeText( "phone", parsedPhone?.number ?? value );
        } );

        // nationality
        if (this.nationalityComboBox) this.errorNationalityView.hidden = true;
        if (this.nationalityComboBox) {
          this.nationalityComboBox.addTarget( this, this.onNationalityChange, UIControl.Event.valueChanged );
          DLDB.shared.countries( (code: number, response: any) => {
            if (code === 200) {
              for (let country of response.data) {
                this.nationalityComboBox.addItem(country.name, country.iso2);
              }
              //this.nationalityComboBox.selectItem(DLDB.shared.defaultCountryCode);
            }
          });
        }

        // alergens
        this.allergiesCB?.addTarget( this, this.onHasAllergiesCheck, UIControl.Event.touchUpInside );
        if (this.allergiesTextArea) this.allergiesTextArea.setOnChangeText( this, function(this:BookingClientViewController, control:UITextArea, value:string) { this.onChangeText( "allergens", value ) } );
        if (this.allergiesTextArea) this.allergiesTextArea.hidden = true;
        if(this.allergiesTextArea) this.allergiesTextArea.enabled = false ;

        // message
        if (this.messageTextArea) this.messageTextArea.setOnChangeText( this, function(this:BookingClientViewController, control:UITextArea, value:string) { this.onChangeText( "comments", value ) } );

        // optional segmented
        if (this.segmentedControl) {
          let options = DLDB.shared.clientCommentOptions;
          if (options) {
            this.segmentedControl.addTarget( this, this.onSegmentedControlChange, UIControl.Event.valueChanged );
            for (let o of options.Values) {
              this.segmentedControl.insertSegmentedItemWithTitle(o, this.segmentedControl.segmentedItems.count, false);
            }
            if (options.DefaultValueIndex != null) this.segmentedControl.selectSegmentedAtIndex(options.DefaultValueIndex);
          }
        }

        // hide non-standard fields by default (every block is optional in the venue layout)
        if (this.languageView) this.languageView.hidden = true;
        if (this.couponView) this.couponView.hidden = true;
        if (this.nationalityView) this.nationalityView.hidden = !DLDB.shared.nationalityFieldActive;
        if (this.optionsView) this.optionsView.hidden = !DLDB.shared.clientCommentOptions;

        this.setErrors( [] ) ;
    }

    viewDidAppear(animated?: any): void {
      super.viewDidAppear(animated);
    }

    private onChangeText ( field: string, value: string )
    {
      this[ field ] = value ;      
      this.clearError( field ) ;
      this.notify( ) ;

      if ( field === "message" ) {
        DLDB.shared.cart().comments = value;
      }
      else { 
        DLDB.shared.cart()[ field ] = value; 
      }
    }

    private onHasAllergiesCheck ( )
    {
      const value = this.allergiesCB.on ;

      this.allergies = value ;
      if (this.allergiesTextArea) this.allergiesTextArea.hidden = !value ;
    }

    private onSegmentedControlChange ( control: UISegmentedControl ) {
      let v = control.segmentedItems[ control.selectedSegmentIndex ].title;
      DLDB.shared.booking().hallComments = v.trim().length > 0 ? v : null ;
      this.check( );
      this.notify();
    }

    private onPhoneCountryChange ( control: MUIComboBox ) {
      this.phoneCountryISO2 = control.selectedItem;
      this.check( );
      this.notify();
    }

    private setPhoneCountrySelectedShortText ( selectEl: HTMLSelectElement ) {
      for (let i = 0; i < selectEl.options.length; i++) {
        const opt  = selectEl.options[i] ;
        const iso2 = opt.value ;
        const cc   = this.phoneCountryCallingCodes[ iso2 ] ;
        const name = this.phoneCountryNames[ iso2 ] ;
        if (cc && name) {
          opt.text = opt.selected ? "+" + cc : name + " (+" + cc + ")" ;
        }
      }
    }

    private onNationalityChange ( control: MUIComboBox ) {
      DLDB.shared.booking().countryISO2 = control.selectedItem;
      this.check( );
      this.notify();
    }

    notify ( )
    {      
      this.delegate?.onClientFormDidChange( this ) ;
    }

    dryCheck ( )
    {
      const errors = []
          , checkIsValid = ( field: string, valid: boolean ) =>
            {
              if ( !valid )
                   errors.push( field ) ;

              return valid ;
            } ;

      checkIsValid( "name"   , this.name.length > 2       ) ;
      checkIsValid( "email"  , isValidEmail( this.email ) ) ;      
      checkIsValid( "phone"  , this.phone.length > 0 ) ;

        // check nationality field Active
        if ( DLDB.shared.nationalityFieldActive == true ) {
            checkIsValid( "nationality", DLDB.shared.booking().countryISO2 != null );
        }
    
        // Check custom options
        if (DLDB.shared.clientCommentOptions && DLDB.shared.clientCommentOptions.Optional == false && DLDB.shared.booking().hallComments == null) {
          errors.push( "options" ) ;
        }
        
      return errors ;
    }
    
    check ( ) {
      const errors = this.dryCheck( ) ;

      if ( !(UIApplication.shared.delegate as AppDelegate).dryRunCheck ) this.setErrors( errors ) ;

      return errors.length === 0 ;
    }

    hasValue ( ) : boolean { return this.dryCheck().length === 0; }

    
    setErrors ( errors: string[] )
    {
      const errorSpec = this.errorSpec( ) ;

      for ( const err in errorSpec )
            if ( errorSpec[ err ] != null ) this.clearError( err ) ;

      for ( const err of errors )
            if ( err in errorSpec && errorSpec[ err ] != null )
                 errorSpec[ err ].hidden = false ;
    }


    clearError ( error: string )
    {
      const errorSpec = this.errorSpec( ) ;

      if ( errorSpec.hasOwnProperty( error ) && errorSpec[ error ] != null )
           errorSpec[ error ].hidden = true ;
    }


    private errorSpec ( )
    {
      return { "name"   : this.errorNameView
             , "email"  : this.errorEmailView
             , "phone"  : this.errorPhoneView
             , "options": this.errorOptionsView
             , "nationality": this.errorNationalityView
             }
    }

    public asJSON ( )
    {
      return { "name": this.name
             , "phone": this.phone
             , "phoneCountryISO2": this.phoneCountryISO2
             , "email": this.email
             , "comments": this.messageTextArea.text
             , "allergies": this.allergies? this.allergens : ""
             , "allergens": this.allergiesTextArea?.text
             } ;
    }

    public clear ( )
    {
      this.name    = "" ;
      this.email   = "" ;
      this.phone   = "" ;
      this.message = "" ;
      this.allergies = false ;
      this.allergens = "" ;

      this.nameTextField.text = ""  ;
      this.phoneTextField.text =  "" ;
      this.emailTexteField.text = "" ;
      this.messageTextArea.text = "" ;
      this.allergiesCB.on = false ;
      if (this.allergiesTextArea) this.allergiesTextArea.text = "" ;
      if (this.segmentedControl != null && this.segmentedControl.segmentedItems.count > 0 ) this.segmentedControl.selectSegmentedAtIndex(0);

      this.setErrors( [] ) ; 
    }
}